/**
 * Show styles on the wire (D32, PLAN §5.1): a tenant-level library of what an
 * operator plays — piece types, the abstract timeline layers their templates
 * target, source/output layers, adlib panels, macro actions and (reserved)
 * MOS ingest rules (D31) — shared by many studios. A studio maps the show
 * style's layers to its devices (`studios.ts`); a production picks a studio
 * and a show style and snapshots both (`productions.ts`).
 *
 * **The contract carries the envelope.** `config` is an opaque JSON object
 * (`configDocument.ts`); what a valid show style is belongs to the engine
 * (`@acp/engine/showStyle.js`), and the handlers validate it with an injected
 * validator — a failure is `validation-failed` with issues under `config.`.
 *
 * **A tenant-level resource.** It belongs to no site or studio, so writing
 * one needs `device.configure` through a TENANT-WIDE grant, and reading needs
 * `rundown.read` anywhere (an operator scoped to one studio still runs its
 * productions' show styles).
 *
 * **Optimistic concurrency**, as studios: every write bumps `version`; an
 * update names the version it was made from (`conflict` otherwise), a delete
 * may (`?version=`). A show style that is a studio's default cannot be
 * deleted (`conflict`, naming the studios); productions are unaffected by a
 * delete — they run from their snapshot (D22).
 */
import { z } from 'zod';
import { configDocumentWireSchema, MAX_SHOW_STYLE_CONFIG_CHARS } from './configDocument.js';
import { isoTimestampWireSchema, showStyleIdWireSchema, studioIdWireSchema } from './primitives.js';

/** The show style config document: a JSON object, otherwise opaque to the contract (the module comment). */
export const showStyleConfigWireSchema = configDocumentWireSchema(MAX_SHOW_STYLE_CONFIG_CHARS, 'show style config');

const nameSchema = z.string().trim().min(1).max(200);

/** A show style without its config: what a listing shows. */
export const showStyleSummaryWireSchema = z
  .object({
    showStyleId: showStyleIdWireSchema,
    name: nameSchema,
    /** 1 on create, +1 per write: the optimistic-concurrency token. */
    version: z.number().int().positive(),
    createdAt: isoTimestampWireSchema,
    updatedAt: isoTimestampWireSchema,
    /** Who wrote this version (the token's subject). */
    updatedBy: z.string().min(1).max(128),
  })
  .strict();
export type ShowStyleSummaryWire = z.infer<typeof showStyleSummaryWireSchema>;

/** A show style with its config. */
export const showStyleWireSchema = showStyleSummaryWireSchema.extend({ config: showStyleConfigWireSchema }).strict();
export type ShowStyleWire = z.infer<typeof showStyleWireSchema>;

export const createShowStyleRequestWireSchema = z
  .object({
    /** Client-chosen id (a retried create returns the show style as it is); the server mints a ULID when absent. */
    showStyleId: showStyleIdWireSchema.optional(),
    name: nameSchema,
    config: showStyleConfigWireSchema,
  })
  .strict();

export const updateShowStyleRequestWireSchema = z
  .object({
    /** The version this update was made from; the stored one must still be it (`conflict` otherwise). */
    version: z.number().int().positive(),
    name: nameSchema,
    config: showStyleConfigWireSchema,
  })
  .strict();

export const showStylePathWireSchema = z.object({ showStyleId: showStyleIdWireSchema.describe('The show style’s id.') }).strict();

export const deleteShowStyleQueryWireSchema = z
  .object({
    version: z.coerce.number().int().positive().optional().describe('Delete only if the show style is still at this version (`conflict` otherwise).'),
  })
  .strict();

export const showStyleResponseWireSchema = z.object({ showStyle: showStyleWireSchema }).strict();
export const listShowStylesResponseWireSchema = z.object({ showStyles: z.array(showStyleSummaryWireSchema) }).strict();

/** `GET /show-styles/{showStyleId}/validate?studioId=`: would this pair make a valid production (D32)? */
export const validatePairQueryWireSchema = z.object({ studioId: studioIdWireSchema.describe('The studio to judge the show style against.') }).strict();

/** One show-style layer against the studio (the engine's `LayerMappingStatus`): what the mapping table shows. */
export const layerMappingWireSchema = z
  .object({
    layer: z.string().min(1).max(128),
    label: z.string().max(200),
    /** The drivers the layer allows; empty = any. */
    deviceTypes: z.array(z.string().min(1).max(64)),
    /** The piece types whose templates target it; empty = unused. */
    usedBy: z.array(z.string().min(1).max(128)),
    mapping: z.object({ device: z.string().min(1).max(128), deviceType: z.string().min(1).max(64).nullable() }).strict().nullable(),
    problem: z.string().nullable(),
  })
  .strict();
export type LayerMappingWire = z.infer<typeof layerMappingWireSchema>;

export const validatePairResponseWireSchema = z
  .object({
    /** True when a production of this pair can be created. */
    valid: z.boolean(),
    /** Every problem of the pair, each at a dotted path starting with the document to fix: `showStyle.`, `studio.` (or `resolved.`). */
    problems: z.array(z.object({ path: z.string(), message: z.string() }).strict()),
    /** Every layer the show style declares, against the studio's mappings (empty when either document itself is invalid). */
    layers: z.array(layerMappingWireSchema),
    /** The versions judged. */
    studioVersion: z.number().int().positive(),
    showStyleVersion: z.number().int().positive(),
  })
  .strict();
export type ValidatePairResponseWire = z.infer<typeof validatePairResponseWireSchema>;
